import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { CheckCircle2, Loader2, Clock, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function PaymentSuccess() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { refresh } = useAuth();
  const [state, setState] = useState("checking"); // checking | paid | pending | error
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let stop = false;
    const poll = async () => {
      if (stop) return;
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState("paid");
          try { await refresh(); } catch { /* ignore */ }
          return;
        }
        if (["expired", "failed"].includes(data.payment_status)) { setState("error"); return; }
      } catch { /* keep polling */ }
      if (attempts.current >= 10) { setState("pending"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { stop = true; };
  }, [sessionId, refresh]);

  const Icon = state === "paid" ? CheckCircle2 : state === "pending" ? Clock : Loader2;

  return (
    <div className="min-h-screen starfield flex items-center justify-center px-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
        className="glass stats-wow rounded-3xl p-10 max-w-md w-full text-center"
        data-testid="payment-success"
      >
        <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
          <Icon className={`text-primary ${state === "checking" ? "animate-spin" : ""}`} size={30} />
        </div>
        {state === "checking" && (
          <p className="font-display italic text-foreground/70 text-xl" data-testid="payment-checking">{t("landing.payment.verifying")}</p>
        )}
        {state === "paid" && (
          <>
            <h1 className="font-display text-gradient-orange text-3xl sm:text-4xl italic mb-4" data-testid="payment-paid">{t("landing.payment.successTitle")}</h1>
            <p className="text-foreground/60 leading-relaxed mb-8">{t("landing.payment.successBody")}</p>
            <Link to="/atelier" data-testid="payment-atelier-btn" className="btn-frost-orange inline-flex items-center gap-2 px-8 py-3.5 rounded-full font-semibold text-sm">
              {t("landing.payment.cta")} <ArrowRight size={16} />
            </Link>
          </>
        )}
        {state === "pending" && (
          <>
            <h1 className="font-display text-gradient-orange text-3xl italic mb-4">{t("landing.payment.pendingTitle")}</h1>
            <p className="text-foreground/60 leading-relaxed mb-8">{t("landing.payment.pendingBody")}</p>
            <Link to="/atelier" className="btn-frost-orange inline-flex items-center gap-2 px-8 py-3.5 rounded-full font-semibold text-sm">{t("landing.payment.cta")}</Link>
          </>
        )}
        {state === "error" && (
          <>
            <h1 className="font-display text-3xl italic mb-4 text-foreground/80">{t("landing.payment.errorTitle")}</h1>
            <Link to="/tarifs" className="text-primary hover:underline text-sm">{t("landing.payment.backTarifs")}</Link>
          </>
        )}
      </motion.div>
    </div>
  );
}
